import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import base_url from './config/Config';
import Sidebar from './components/Sidebar';
import Navigation from './components/Navigation';
import axios from "axios";
import Spinner from 'react-bootstrap/Spinner';
import { Modal, Button } from 'react-bootstrap';
import PageLoader from './components/PageLoader';
import Cookies from 'js-cookie';
import {
    BsFillGrid3X3GapFill, BsPatchCheckFill, BsFillXOctagonFill, BsPersonCircle, BsClockHistory, BsClipboard, BsClipboardCheck,
} from 'react-icons/bs';

const PENDING_STATUSES = ['CREATED', 'AUTHORIZED'];
const FAILED_STATUSES = ['REJECTED', 'FAILED', 'CANCELLED'];
const RETRYABLE_STATUSES = ['NOT_CREATED', ...FAILED_STATUSES];

const MANDATE_STATUS_OPTIONS = ['NOT_CREATED', 'CREATED', 'AUTHORIZED', 'CONFIRMED', 'REJECTED', 'CANCELLED', 'FAILED'];

const TABS = [
    { key: 'all', label: 'All Mandates' },
    { key: 'pending', label: 'Pending' },
    { key: 'success', label: 'Success' },
    { key: 'failed', label: 'Failed' },
];

// Plain-language label + hover explanation for every status code this page
// shows — Razorpay's own status names (CONFIRMED/AUTHORIZED/etc.) mean
// nothing to someone who isn't reading their API docs, so translate them.
const STATUS_MEANING = {
    NOT_CREATED: { label: 'Not Registered', hint: 'Customer has not set up auto-debit yet. Use "Send Mandate Link" to email them the registration link.' },
    CREATED: { label: 'Link Sent', hint: 'Registration link has been emailed to the customer — waiting for them to pay/authorize.' },
    AUTHORIZED: { label: 'Bank Confirming', hint: 'Customer has authorized on their end — waiting for final confirmation from their bank/NPCI.' },
    CONFIRMED: { label: 'Active', hint: 'Auto-debit is live — the EMI will be deducted automatically on the due date. You can also charge it instantly with "Charge Now".' },
    REJECTED: { label: 'Rejected by Bank', hint: "The customer's bank rejected the mandate. Use \"Send Mandate Link\" to try again." },
    CANCELLED: { label: 'Cancelled', hint: 'This mandate was cancelled. Use "Send Mandate Link" to register a new one.' },
    FAILED: { label: 'Failed', hint: 'Registration failed (link expired or an error occurred). Use "Send Mandate Link" to try again.' },
    Overdue: { label: 'Overdue', hint: 'This EMI is past its due date and still unpaid.' },
    Pending: { label: 'Upcoming', hint: 'This EMI is not due yet — it is due in the future.' },
    'None due': { label: 'No EMI Due', hint: 'This loan has no pending EMI right now.' },
    SUCCESS: { label: 'Charged', hint: "This charge attempt succeeded — the amount was deducted from the customer's account." },
    SENT: { label: 'Sent', hint: 'The reminder email was sent to the customer successfully.' },
};

const mandateStatusLabel = (status) => STATUS_MEANING[status]?.label ?? status;
const statusHint = (status) => STATUS_MEANING[status]?.hint ?? '';

// Bootstrap badge class per status — same bg-* convention Loan.jsx already uses.
const statusBadgeClass = (status) => {
    switch (status) {
        case 'CONFIRMED':
        case 'SUCCESS':
        case 'SENT':
            return 'bg-success';
        case 'REJECTED':
        case 'FAILED':
        case 'CANCELLED':
        case 'Overdue':
            return 'bg-danger';
        case 'CREATED':
        case 'AUTHORIZED':
        case 'Pending':
            return 'bg-warning text-dark';
        case 'NOT_CREATED':
            return 'bg-secondary';
        default:
            return 'bg-light text-dark';
    }
};

const StatusBadge = ({ status, label }) => (
    <span className={`badge ${statusBadgeClass(status)}`} title={statusHint(status)}>
        {label ?? mandateStatusLabel(status) ?? '-'}
    </span>
);

const formatDateTime = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const SummaryCard = ({ icon, label, value, colorClass, hint }) => (
    <div className='col-xl-3 col-md-4 col-sm-6 mb-3'>
        <div className={`stat-card ${colorClass}`} title={hint}>
            <div className="stat-icon-wrap">{icon}</div>
            <div className="stat-info">
                <p className="stat-label">{label}</p>
                <h3 className="stat-value">{value}</h3>
            </div>
        </div>
    </div>
);

const AutoDebitMandates = () => {
    const [mandates, setMandates] = useState([]);
    const [filteredMandates, setFilteredMandates] = useState([]);
    const [isDownloading, setIsDownloading] = useState(false);
    const [chargingLoanId, setChargingLoanId] = useState(null);
    const [sendingLinkLoanId, setSendingLinkLoanId] = useState(null);
    const [sendingReminderLoanId, setSendingReminderLoanId] = useState(null);
    const [permissions, setPermissions] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchOption, setSearchOption] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [pageLoading, setPageLoading] = useState(true);
    const [summary, setSummary] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const validTab = (t) => TABS.some(tb => tb.key === t) ? t : 'all';
    const [activeTab, setActiveTab] = useState(validTab(searchParams.get('tab')));
    const [manualLoanId, setManualLoanId] = useState('');
    const [manualSending, setManualSending] = useState(false);
    const [historyLoanId, setHistoryLoanId] = useState(null);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [chargeHistory, setChargeHistory] = useState([]);
    const [notificationHistory, setNotificationHistory] = useState([]);
    const [selectedLoanIds, setSelectedLoanIds] = useState(new Set());
    const [bulkRunning, setBulkRunning] = useState(false);
    const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });
    const [linkByLoanId, setLinkByLoanId] = useState({});
    const [copiedLoanId, setCopiedLoanId] = useState(null);
    const [showHelp, setShowHelp] = useState(false);
    const perPage = 50;

    const authHeader = () => {
        const token = Cookies.get('authToken');
        return token ? { Authorization: `Bearer ${token}` } : {};
    };

    // Permissions
    useEffect(() => {
        const adminId = localStorage.getItem('adminId') || '';
        axios.get(`${base_url}/api/auth/tabs/${adminId}`)
            .then(res => setPermissions(res.data || []))
            .catch(() => setPermissions([]))
            .finally(() => setPageLoading(false));
    }, []);

    const hasPermission = useCallback((tabName, action) => {
        const tab = permissions.find(t => t.tabName === tabName);
        if (!tab) return false;
        switch (action) {
            case 'view': return !!tab.canView;
            case 'edit': return !!tab.canEdit;
            case 'download': return !!tab.canDelete; // using canDelete for download, same as rest of app
            default: return false;
        }
    }, [permissions]);

    const getAllMandates = useCallback(() => {
        if (!hasPermission('Auto Debit Mandate', 'view')) return;
        setPageLoading(true);
        axios.get(`${base_url}/api/razorpay-mandate-dashboard/list`, { headers: authHeader() })
            .then((response) => {
                setMandates(response.data);
                setFilteredMandates(response.data);
            })
            .catch((error) => console.log(error))
            .finally(() => setPageLoading(false));
    }, [hasPermission]);

    const getSummary = useCallback(() => {
        if (!hasPermission('Auto Debit Mandate', 'view')) return;
        axios.get(`${base_url}/api/razorpay-mandate-dashboard/summary`, { headers: authHeader() })
            .then((response) => setSummary(response.data))
            .catch((error) => console.log(error));
    }, [hasPermission]);

    useEffect(() => {
        getAllMandates();
        getSummary();
    }, [getAllMandates, getSummary]);

    // Sidebar sub-tabs ("All Mandates" / "Success") link here with ?tab=... —
    // keep activeTab in sync whenever that changes (including re-clicking the
    // same sidebar sub-tab while already on this page).
    useEffect(() => {
        setActiveTab(validTab(searchParams.get('tab')));
        setCurrentPage(1);
        setSelectedLoanIds(new Set());
    }, [searchParams]);

    // Export excel
    const handleExport = async () => {
        if (!hasPermission('Auto Debit Mandate', 'download')) {
            alert('You do not have permission to download this.');
            return;
        }
        setIsDownloading(true);
        try {
            const response = await axios.get(`${base_url}/api/razorpay-mandate-dashboard/export`, {
                responseType: 'blob',
                headers: authHeader(),
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'Auto_Debit_Mandates.xlsx');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            alert('Export successful!');
        } catch (error) {
            console.error('Error exporting mandates:', error.response?.data || error.message);
            alert('Export failed');
        } finally {
            setIsDownloading(false);
        }
    };

    // Manual "Charge Now" — for a loan whose mandate is already CONFIRMED/registered
    const handleChargeNow = async (loanId) => {
        if (!hasPermission('Auto Debit Mandate', 'edit')) {
            alert('You do not have permission to charge mandates.');
            return;
        }
        if (!window.confirm(`Charge the due EMI for loan #${loanId} right now via the registered mandate?`)) return;

        setChargingLoanId(loanId);
        try {
            const response = await axios.post(
                `${base_url}/api/razorpay-mandate-dashboard/charge-now/${loanId}`,
                {},
                { headers: authHeader() }
            );
            if (response.data?.status === 'SUCCESS') {
                alert(`Charged successfully for loan #${loanId}`);
            } else {
                alert(`Charge attempt failed for loan #${loanId}: ${response.data?.errorDescription || 'Unknown error'}`);
            }
            getAllMandates();
            getSummary();
        } catch (error) {
            alert(`Charge failed: ${error.response?.data?.error || error.message}`);
        } finally {
            setChargingLoanId(null);
        }
    };

    // Manual "Send Mandate Link" — for a loan that has no mandate yet (or a
    // failed/rejected one). Generates a fresh link via ujjwalsrijanapp and
    // emails it to the customer.
    const handleSendLink = async (loanId) => {
        if (!hasPermission('Auto Debit Mandate', 'edit')) {
            alert('You do not have permission to send mandate links.');
            return;
        }
        if (!window.confirm(`Generate and email the mandate registration link for loan #${loanId}?`)) return;

        setSendingLinkLoanId(loanId);
        try {
            const response = await axios.post(
                `${base_url}/api/razorpay-mandate-dashboard/send-link/${loanId}`,
                {},
                { headers: authHeader() }
            );
            if (response.data?.paymentLinkUrl) {
                setLinkByLoanId(prev => ({ ...prev, [loanId]: response.data.paymentLinkUrl }));
            }
            alert(response.data?.message || 'Mandate link generated.');
            getAllMandates();
            getSummary();
        } catch (error) {
            alert(`Failed to send mandate link: ${error.response?.data?.error || error.message}`);
        } finally {
            setSendingLinkLoanId(null);
        }
    };

    // Copy a mandate link to the clipboard — either just-generated (session
    // state) or the last known one the backend already had on file (falls
    // back to a prompt() if the browser blocks clipboard access, e.g. non-HTTPS).
    const handleCopyLink = async (loanId, url) => {
        if (!url) return;
        try {
            await navigator.clipboard.writeText(url);
            setCopiedLoanId(loanId);
            setTimeout(() => setCopiedLoanId(null), 2000);
        } catch (error) {
            window.prompt('Copy this link:', url);
        }
    };

    const isBulkEligible = useCallback((m) => (
        activeTab === 'success' ? !!m.nextEmiId : RETRYABLE_STATUSES.includes(m.mandateStatus)
    ), [activeTab]);

    // Manual "Send Mandate Link" by typing a Loan ID directly — works for any
    // loan regardless of what's currently visible in the table (search/page/tab).
    const handleManualSendLink = async (event) => {
        event.preventDefault();
        if (!hasPermission('Auto Debit Mandate', 'edit')) {
            alert('You do not have permission to send mandate links.');
            return;
        }
        const loanId = manualLoanId.trim();
        if (!loanId) return;
        setManualSending(true);
        try {
            const response = await axios.post(
                `${base_url}/api/razorpay-mandate-dashboard/send-link/${loanId}`, {}, { headers: authHeader() }
            );
            if (response.data?.paymentLinkUrl) {
                setLinkByLoanId(prev => ({ ...prev, [loanId]: response.data.paymentLinkUrl }));
            }
            alert(response.data?.message || `Mandate link generated for loan #${loanId}.`);
            setManualLoanId('');
            getAllMandates();
            getSummary();
        } catch (error) {
            alert(`Failed to send mandate link: ${error.response?.data?.error || error.message}`);
        } finally {
            setManualSending(false);
        }
    };

    const toggleSelectOne = (loanId) => {
        setSelectedLoanIds(prev => {
            const next = new Set(prev);
            if (next.has(loanId)) next.delete(loanId); else next.add(loanId);
            return next;
        });
    };

    // Bulk "Charge Now" — runs sequentially (not parallel) so a burst of
    // manual charges doesn't hammer Razorpay's API all at once.
    const handleBulkChargeNow = async () => {
        if (!hasPermission('Auto Debit Mandate', 'edit')) {
            alert('You do not have permission to charge mandates.');
            return;
        }
        const ids = [...selectedLoanIds];
        if (ids.length === 0) return;
        if (!window.confirm(`Charge Now for ${ids.length} selected loan(s)?`)) return;

        setBulkRunning(true);
        setBulkProgress({ done: 0, total: ids.length });
        let success = 0, failed = 0;
        for (const loanId of ids) {
            try {
                const response = await axios.post(
                    `${base_url}/api/razorpay-mandate-dashboard/charge-now/${loanId}`, {}, { headers: authHeader() }
                );
                if (response.data?.status === 'SUCCESS') success++; else failed++;
            } catch (error) {
                failed++;
            }
            setBulkProgress(p => ({ ...p, done: p.done + 1 }));
        }
        setBulkRunning(false);
        alert(`Charge Now complete: ${success} succeeded, ${failed} failed out of ${ids.length}.`);
        setSelectedLoanIds(new Set());
        getAllMandates();
        getSummary();
    };

    // Bulk "Send Reminder".
    const handleBulkSendReminder = async () => {
        if (!hasPermission('Auto Debit Mandate', 'edit')) {
            alert('You do not have permission to send reminders.');
            return;
        }
        const ids = [...selectedLoanIds];
        if (ids.length === 0) return;
        if (!window.confirm(`Send reminder for ${ids.length} selected loan(s)?`)) return;

        setBulkRunning(true);
        setBulkProgress({ done: 0, total: ids.length });
        let sent = 0, failed = 0;
        for (const loanId of ids) {
            try {
                const response = await axios.post(
                    `${base_url}/api/razorpay-mandate-dashboard/notify/${loanId}`, {}, { headers: authHeader() }
                );
                if (response.data?.status === 'SENT') sent++; else failed++;
            } catch (error) {
                failed++;
            }
            setBulkProgress(p => ({ ...p, done: p.done + 1 }));
        }
        setBulkRunning(false);
        alert(`Reminders complete: ${sent} sent, ${failed} failed out of ${ids.length}.`);
        setSelectedLoanIds(new Set());
        getAllMandates();
    };

    // Bulk "Send Mandate Link" — only for NOT_CREATED / FAILED / REJECTED / CANCELLED rows.
    const handleBulkSendLink = async () => {
        if (!hasPermission('Auto Debit Mandate', 'edit')) {
            alert('You do not have permission to send mandate links.');
            return;
        }
        const ids = [...selectedLoanIds];
        if (ids.length === 0) return;
        if (!window.confirm(`Generate and email mandate links for ${ids.length} selected loan(s)?`)) return;

        setBulkRunning(true);
        setBulkProgress({ done: 0, total: ids.length });
        let success = 0, failed = 0;
        const newLinks = {};
        for (const loanId of ids) {
            try {
                const response = await axios.post(
                    `${base_url}/api/razorpay-mandate-dashboard/send-link/${loanId}`, {}, { headers: authHeader() }
                );
                if (response.data?.paymentLinkUrl) {
                    newLinks[loanId] = response.data.paymentLinkUrl;
                    success++;
                } else {
                    failed++;
                }
            } catch (error) {
                failed++;
            }
            setBulkProgress(p => ({ ...p, done: p.done + 1 }));
        }
        setLinkByLoanId(prev => ({ ...prev, ...newLinks }));
        setBulkRunning(false);
        alert(`Mandate links complete: ${success} generated, ${failed} failed out of ${ids.length}.`);
        setSelectedLoanIds(new Set());
        getAllMandates();
        getSummary();
    };

    // Manual "Send Reminder" — resends the EMI-due reminder for this loan's
    // next unpaid EMI right now instead of waiting for the automatic cron.
    const handleSendReminder = async (loanId) => {
        if (!hasPermission('Auto Debit Mandate', 'edit')) {
            alert('You do not have permission to send reminders.');
            return;
        }
        setSendingReminderLoanId(loanId);
        try {
            const response = await axios.post(
                `${base_url}/api/razorpay-mandate-dashboard/notify/${loanId}`,
                {},
                { headers: authHeader() }
            );
            if (response.data?.status === 'SENT') {
                alert(`Reminder sent for loan #${loanId}`);
            } else {
                alert(`Reminder failed for loan #${loanId}: ${response.data?.errorMessage || 'Unknown error'}`);
            }
            getAllMandates();
        } catch (error) {
            alert(`Failed to send reminder: ${error.response?.data?.error || error.message}`);
        } finally {
            setSendingReminderLoanId(null);
        }
    };

    // "History" — combined charge-attempt + reminder-notification log for one loan.
    const handleViewHistory = async (loanId) => {
        setHistoryLoanId(loanId);
        setHistoryLoading(true);
        setChargeHistory([]);
        setNotificationHistory([]);
        try {
            const [chargesRes, notifsRes] = await Promise.all([
                axios.get(`${base_url}/api/razorpay-mandate-dashboard/history/${loanId}`, { headers: authHeader() }),
                axios.get(`${base_url}/api/razorpay-mandate-dashboard/notifications/${loanId}`, { headers: authHeader() }),
            ]);
            setChargeHistory(chargesRes.data || []);
            setNotificationHistory(notifsRes.data || []);
        } catch (error) {
            console.error('Error loading history:', error);
        } finally {
            setHistoryLoading(false);
        }
    };

    const handleSearch = (event) => {
        event.preventDefault();
        let filtered = [...mandates];
        if (searchOption && searchInput) {
            switch (searchOption) {
                case 'loan_id':
                    filtered = filtered.filter(m => m.loanId?.toString() === searchInput);
                    break;
                case 'customer_id':
                    filtered = filtered.filter(m => m.customerId?.toString() === searchInput);
                    break;
                case 'customer_name':
                    filtered = filtered.filter(m => m.customerName?.toLowerCase().includes(searchInput.toLowerCase()));
                    break;
                case 'mobile':
                    filtered = filtered.filter(m => m.mobileNumber?.toString().includes(searchInput));
                    break;
                case 'status':
                    filtered = filtered.filter(m => m.mandateStatus === searchInput);
                    break;
                default:
                    break;
            }
        }
        setFilteredMandates(filtered);
        setCurrentPage(1);
    };

    const pendingMandates = filteredMandates.filter(m => PENDING_STATUSES.includes(m.mandateStatus));
    const successMandates = filteredMandates.filter(m => m.mandateStatus === 'CONFIRMED');
    const failedMandates = filteredMandates.filter(m => FAILED_STATUSES.includes(m.mandateStatus));
    const rowsForTab = activeTab === 'pending' ? pendingMandates
        : activeTab === 'success' ? successMandates
        : activeTab === 'failed' ? failedMandates
        : filteredMandates;

    const indexOfLast = currentPage * perPage;
    const indexOfFirst = indexOfLast - perPage;
    const currentRows = rowsForTab.slice(indexOfFirst, indexOfLast);
    const totalPages = Math.ceil(rowsForTab.length / perPage);
    const maxPageButtons = 10;

    const eligibleRowsOnPage = currentRows.filter(isBulkEligible);
    const eligibleRowsInTab = rowsForTab.filter(isBulkEligible);
    const allOnPageSelected = eligibleRowsOnPage.length > 0 && eligibleRowsOnPage.every(m => selectedLoanIds.has(m.loanId));

    const toggleSelectAllOnPage = () => {
        setSelectedLoanIds(prev => {
            const next = new Set(prev);
            if (allOnPageSelected) {
                eligibleRowsOnPage.forEach(m => next.delete(m.loanId));
            } else {
                eligibleRowsOnPage.forEach(m => next.add(m.loanId));
            }
            return next;
        });
    };

    const switchTab = (tab) => {
        setActiveTab(tab);
        setCurrentPage(1);
        setSelectedLoanIds(new Set());
        setSearchParams({ tab });
    };

    const getPaginationRange = () => {
        const mid = Math.floor(maxPageButtons / 2);
        let start = Math.max(1, currentPage - mid);
        let end = Math.min(totalPages, start + maxPageButtons - 1);
        if (end - start < maxPageButtons - 1) start = Math.max(1, end - maxPageButtons + 1);
        return { start, end };
    };
    const { start: startPage, end: endPage } = getPaginationRange();

    if (pageLoading) return <PageLoader text="Loading Auto-Debit Mandates..." />;

    if (!hasPermission('Auto Debit Mandate', 'view')) {
        return (
            <div className="text-center mt-5">
                <h3>Access Denied</h3>
                <p>You do not have permission to view this page.</p>
            </div>
        );
    }

    return (
        <>
            <div className='overviewsaa'>
                <h4>Auto-Debit Mandates (Razorpay)</h4>
                <p style={{ fontSize: '13px', color: '#666', margin: '2px 0 0' }}>
                    Set up automatic EMI deduction from a customer's bank account, charge an EMI instantly, and send payment reminders.
                    {' '}
                    <button type="button" className="btn btn-link btn-sm p-0 align-baseline"
                        onClick={() => setShowHelp(s => !s)}>
                        {showHelp ? 'Hide' : 'How does this work?'}
                    </button>
                </p>
                {showHelp && (
                    <div style={{
                        background: '#f8f9fa', border: '1px solid #e2e2e2', borderRadius: 8,
                        padding: '14px 18px', marginTop: 8, fontSize: '13px', textAlign: 'left',
                    }}>
                        <b>How to use this page:</b>
                        <ol style={{ marginBottom: 10, marginTop: 6, paddingLeft: 18 }}>
                            <li>The <b>All Mandates</b> tab lists every loan. For anyone not yet registered, click <b>"Send Mandate Link"</b> to email them a link — they'll authorize auto-debit from their bank on that link.</li>
                            <li>Once the customer authorizes and their bank confirms it, the status becomes <b>"Active"</b> and the loan moves into the <b>Success</b> tab.</li>
                            <li>In the <b>Success</b> tab, use <b>"Charge Now"</b> to deduct the EMI immediately, or <b>"Send Reminder"</b> to email the customer that their EMI is coming up.</li>
                            <li>Click the <BsClockHistory /> icon to see a loan's full history — every charge attempt and every reminder sent.</li>
                            <li>Use the checkboxes to select multiple loans and run an action on all of them at once — "Select all eligible" picks every valid row for you.</li>
                            <li>Hover over any colored status badge to see what it means.</li>
                        </ol>
                        <b>What each status means:</b>
                        <ul style={{ marginBottom: 0, marginTop: 6, paddingLeft: 18 }}>
                            {MANDATE_STATUS_OPTIONS.map(s => (
                                <li key={s}><b>{STATUS_MEANING[s].label}</b> — {STATUS_MEANING[s].hint}</li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
            <div className='navigationss mb-5'>
                <Navigation />
            </div>
            <div className='container-fluid'>
                <div className='row'>
                    <div className='col-lg-2 col-md-2 sidebarnavigartion'>
                        <div className='lmssidebar'>
                            <Sidebar />
                        </div>
                    </div>
                    <div className='col-lg-10 col-md-10'>
                        <div className='dashboardlms text-center'>
                            {summary && (
                                <div className='row'>
                                    <SummaryCard icon={<BsFillGrid3X3GapFill />} colorClass='card-dark'
                                        label='Total Loans' value={summary.totalLoans}
                                        hint='Total loans on the platform, registered or not' />
                                    <SummaryCard icon={<BsPersonCircle />} colorClass='card-blue'
                                        label='Registered' value={`${summary.mandateCreatedCount} (${summary.mandateCreatedPercent}%)`}
                                        hint='Loans that have started or completed mandate registration (at any stage)' />
                                    <SummaryCard icon={<BsPatchCheckFill />} colorClass='card-green'
                                        label='Success' value={`${summary.successCount} (${summary.successPercent}%)`}
                                        hint='Loans whose auto-debit is fully Active (bank has confirmed it)' />
                                    <SummaryCard icon={<BsFillXOctagonFill />} colorClass='card-red'
                                        label='Failed' value={summary.failedCount}
                                        hint='Loans whose registration was rejected by the bank, or failed/cancelled' />
                                </div>
                            )}
                            <div className='row'>
                                <div className='col-md-12'>
                                    <div className='searchtoll'>
                                        <form onSubmit={handleSearch}>
                                            <div className='row'>
                                                <div className="col-md-3">
                                                    <select
                                                        className="form-control"
                                                        value={searchOption}
                                                        onChange={(e) => { setSearchOption(e.target.value); setSearchInput(''); }}
                                                    >
                                                        <option value="">Search By</option>
                                                        <option value="loan_id">Loan ID</option>
                                                        <option value="customer_id">Customer ID</option>
                                                        <option value="customer_name">Customer Name</option>
                                                        <option value="mobile">Mobile Number</option>
                                                        <option value="status">Mandate Status</option>
                                                    </select>
                                                </div>
                                                <div className="col-md-4">
                                                    {searchOption === 'status' ? (
                                                        <select
                                                            className="form-control"
                                                            value={searchInput}
                                                            onChange={(e) => setSearchInput(e.target.value)}
                                                        >
                                                            <option value="">Select status...</option>
                                                            {MANDATE_STATUS_OPTIONS.map(s => (
                                                                <option key={s} value={s}>{mandateStatusLabel(s)}</option>
                                                            ))}
                                                        </select>
                                                    ) : (
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="Search..."
                                                            value={searchInput}
                                                            onChange={(e) => setSearchInput(e.target.value)}
                                                        />
                                                    )}
                                                </div>
                                                <div className="col-md-2">
                                                    <button type="submit" className="btn btn-sm btn-primary">Search</button>
                                                </div>
                                                <div className='col-md-3 mt-2'>
                                                    <div className='export'>
                                                        <button type="button" className="exportbutton" onClick={handleExport}
                                                            disabled={isDownloading || !hasPermission('Auto Debit Mandate', 'download')}>
                                                            {isDownloading ? (
                                                                <Spinner animation="border" size="sm" />
                                                            ) : (
                                                                <>Export</>
                                                            )}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </form>
                                    </div>

                                    {/* Generate a mandate link for any loan directly, without hunting through the table */}
                                    {hasPermission('Auto Debit Mandate', 'edit') && (
                                        <form onSubmit={handleManualSendLink} className='mt-2'>
                                            <div className='row align-items-center'>
                                                <div className="col-md-4 col-6">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Enter Loan ID..."
                                                        value={manualLoanId}
                                                        onChange={(e) => setManualLoanId(e.target.value)}
                                                    />
                                                </div>
                                                <div className="col-md-4 col-6">
                                                    <button type="submit" className="btn btn-sm btn-success"
                                                        disabled={manualSending || !manualLoanId.trim()}>
                                                        {manualSending ? <Spinner animation="border" size="sm" /> : 'Generate & Send Mandate Link'}
                                                    </button>
                                                </div>
                                                <div className="col-md-4 d-none d-md-block">
                                                    <span style={{ fontSize: '12px', color: '#888' }}>
                                                        Works for any loan, even one not registered before.
                                                    </span>
                                                </div>
                                            </div>
                                        </form>
                                    )}
                                </div>

                                <div className='col-md-12 mb-2 mt-2'>
                                    {/* Tab Toggle */}
                                    <div className='loan-tabs'>
                                        {TABS.map(t => (
                                            <button key={t.key}
                                                className={`loan-tab-btn${activeTab === t.key ? ' loan-tab-active' : ''}`}
                                                onClick={() => switchTab(t.key)}
                                            >
                                                {t.label} ({
                                                    t.key === 'pending' ? pendingMandates.length
                                                        : t.key === 'success' ? successMandates.length
                                                        : t.key === 'failed' ? failedMandates.length
                                                        : filteredMandates.length
                                                })
                                            </button>
                                        ))}
                                    </div>
                                    <p style={{ fontSize: '12px', color: '#888', textAlign: 'left', margin: '2px 0 8px' }}>
                                        {activeTab === 'success' && "All these customers have auto-debit active — charge an EMI or send a reminder from here."}
                                        {activeTab === 'pending' && "Link has been sent — waiting on the customer to authorize and/or their bank to confirm it."}
                                        {activeTab === 'failed' && 'Registration was rejected, failed, or cancelled. Use "Send Mandate Link" to try again.'}
                                        {activeTab === 'all' && 'Every loan, registered or not. For anyone not registered yet, send them the mandate link.'}
                                    </p>

                                    {/* Bulk action bar */}
                                    <div className='bulk-action-bar mb-2' style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                        {eligibleRowsInTab.length > 0 && (
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-link"
                                                onClick={() => setSelectedLoanIds(new Set(eligibleRowsInTab.map(m => m.loanId)))}
                                                disabled={bulkRunning}
                                            >
                                                Select all {eligibleRowsInTab.length} eligible
                                            </button>
                                        )}
                                        {selectedLoanIds.size > 0 && (
                                            <>
                                                <span className="badge bg-dark">{selectedLoanIds.size} selected</span>
                                                <button type="button" className="btn btn-sm btn-outline-secondary"
                                                    onClick={() => setSelectedLoanIds(new Set())} disabled={bulkRunning}>
                                                    Clear
                                                </button>
                                                {activeTab === 'success' ? (
                                                    <>
                                                        <button type="button" className="btn btn-sm btn-primary"
                                                            onClick={handleBulkChargeNow} disabled={bulkRunning}>
                                                            Charge Now Selected
                                                        </button>
                                                        <button type="button" className="btn btn-sm btn-secondary"
                                                            onClick={handleBulkSendReminder} disabled={bulkRunning}>
                                                            Send Reminder Selected
                                                        </button>
                                                    </>
                                                ) : (
                                                    <button type="button" className="btn btn-sm btn-primary"
                                                        onClick={handleBulkSendLink} disabled={bulkRunning}>
                                                        Send Mandate Link Selected
                                                    </button>
                                                )}
                                                {bulkRunning && (
                                                    <span>
                                                        <Spinner animation="border" size="sm" className="me-1" />
                                                        Processing {bulkProgress.done}/{bulkProgress.total}...
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </div>

                                    <div className='allcustomer table-responsive table-striped mt-3'>
                                        {currentRows.length > 0 ? (
                                            activeTab === 'success' ? (
                                                <table className="styled-table">
                                                    <tbody>
                                                        <tr>
                                                            <th>
                                                                <input type="checkbox" checked={allOnPageSelected}
                                                                    onChange={toggleSelectAllOnPage} disabled={eligibleRowsOnPage.length === 0} />
                                                            </th>
                                                            <th>S.N.</th>
                                                            <th>Loan_Id</th>
                                                            <th>Customer</th>
                                                            <th>Mobile</th>
                                                            <th>Next_EMI_Due</th>
                                                            <th>Next_EMI_Amount</th>
                                                            <th>Next_EMI_Status</th>
                                                            <th>Reminder_Sent</th>
                                                            <th>Last_Charge</th>
                                                            <th>Actions</th>
                                                        </tr>
                                                        {currentRows.map((m, index) => (
                                                            <tr key={index}>
                                                                <td>
                                                                    <input type="checkbox" checked={selectedLoanIds.has(m.loanId)}
                                                                        onChange={() => toggleSelectOne(m.loanId)}
                                                                        disabled={!isBulkEligible(m)} title={!isBulkEligible(m) ? 'No due EMI' : ''} />
                                                                </td>
                                                                <td>{index + 1 + (currentPage - 1) * perPage}</td>
                                                                <td>{m.loanId}</td>
                                                                <td>{m.customerName} ({m.customerId})</td>
                                                                <td>{m.mobileNumber}</td>
                                                                <td>{m.nextEmiDueDate || '-'}</td>
                                                                <td>{m.nextEmiAmount || '-'}</td>
                                                                <td><StatusBadge status={m.nextEmiStatus} /></td>
                                                                <td>
                                                                    {m.reminderSentForNextEmi
                                                                        ? <span title={formatDateTime(m.reminderSentAt)}>Yes{formatDateTime(m.reminderSentAt) ? ` (${formatDateTime(m.reminderSentAt)})` : ''}</span>
                                                                        : 'No'}
                                                                </td>
                                                                <td>
                                                                    {m.lastChargeStatus ? (
                                                                        <StatusBadge status={m.lastChargeStatus} label={`${m.lastChargeStatus} (₹${m.lastChargeAmount ?? '-'})`} />
                                                                    ) : 'Never charged'}
                                                                </td>
                                                                <td className='iconss'>
                                                                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', flexWrap: 'wrap' }}>
                                                                        {hasPermission('Auto Debit Mandate', 'edit') && (
                                                                            <>
                                                                                <button
                                                                                    className="btn btn-sm btn-outline-primary"
                                                                                    disabled={chargingLoanId === m.loanId || !m.nextEmiId}
                                                                                    title={!m.nextEmiId ? 'This loan has no EMI due right now' : "Deducts the EMI from the customer's bank account right now"}
                                                                                    onClick={() => handleChargeNow(m.loanId)}
                                                                                >
                                                                                    {chargingLoanId === m.loanId ? (
                                                                                        <Spinner animation="border" size="sm" />
                                                                                    ) : (
                                                                                        'Charge Now'
                                                                                    )}
                                                                                </button>
                                                                                <button
                                                                                    className="btn btn-sm btn-outline-secondary"
                                                                                    disabled={sendingReminderLoanId === m.loanId || !m.nextEmiId}
                                                                                    title={!m.nextEmiId ? 'This loan has no EMI due right now' : 'Emails the customer that their EMI is coming up'}
                                                                                    onClick={() => handleSendReminder(m.loanId)}
                                                                                >
                                                                                    {sendingReminderLoanId === m.loanId ? (
                                                                                        <Spinner animation="border" size="sm" />
                                                                                    ) : (
                                                                                        'Send Reminder'
                                                                                    )}
                                                                                </button>
                                                                            </>
                                                                        )}
                                                                        <button
                                                                            className="btn btn-sm btn-outline-dark"
                                                                            title="View this loan's full charge and reminder history"
                                                                            onClick={() => handleViewHistory(m.loanId)}
                                                                        >
                                                                            <BsClockHistory />
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            ) : (
                                                <table className="styled-table">
                                                    <tbody>
                                                        <tr>
                                                            <th>
                                                                <input type="checkbox" checked={allOnPageSelected}
                                                                    onChange={toggleSelectAllOnPage} disabled={eligibleRowsOnPage.length === 0} />
                                                            </th>
                                                            <th>S.N.</th>
                                                            <th>Loan_Id</th>
                                                            <th>Customer</th>
                                                            <th>Mobile</th>
                                                            <th>Mandate_Status</th>
                                                            <th>Auth_Type</th>
                                                            <th>Max_Amount</th>
                                                            <th>Next_EMI_Due</th>
                                                            <th>Next_EMI_Status</th>
                                                            <th>Last_Charge</th>
                                                            <th>Reason</th>
                                                            <th>Action</th>
                                                        </tr>
                                                        {currentRows.map((m, index) => {
                                                            const linkUrl = linkByLoanId[m.loanId] || m.mandateLinkUrl;
                                                            return (
                                                            <tr key={index}>
                                                                <td>
                                                                    <input type="checkbox" checked={selectedLoanIds.has(m.loanId)}
                                                                        onChange={() => toggleSelectOne(m.loanId)}
                                                                        disabled={!isBulkEligible(m)} title={!isBulkEligible(m) ? 'Mandate already registered' : ''} />
                                                                </td>
                                                                <td>{index + 1 + (currentPage - 1) * perPage}</td>
                                                                <td>{m.loanId}</td>
                                                                <td>{m.customerName} ({m.customerId})</td>
                                                                <td>{m.mobileNumber}</td>
                                                                <td><StatusBadge status={m.mandateStatus} /></td>
                                                                <td>{m.authType || '-'}</td>
                                                                <td>{m.mandateMaxAmount || '-'}</td>
                                                                <td>{m.nextEmiDueDate || '-'}</td>
                                                                <td><StatusBadge status={m.nextEmiStatus} /></td>
                                                                <td>
                                                                    {m.lastChargeStatus ? (
                                                                        <StatusBadge status={m.lastChargeStatus} label={`${m.lastChargeStatus} (₹${m.lastChargeAmount ?? '-'})`} />
                                                                    ) : 'Never charged'}
                                                                </td>
                                                                <td>{m.failureReason || '-'}</td>
                                                                <td className='iconss'>
                                                                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', flexWrap: 'wrap' }}>
                                                                        {hasPermission('Auto Debit Mandate', 'edit') && RETRYABLE_STATUSES.includes(m.mandateStatus) && (
                                                                            <button
                                                                                className="btn btn-sm btn-outline-primary"
                                                                                disabled={sendingLinkLoanId === m.loanId}
                                                                                title="Emails the customer a link so they can set up auto-debit"
                                                                                onClick={() => handleSendLink(m.loanId)}
                                                                            >
                                                                                {sendingLinkLoanId === m.loanId ? (
                                                                                    <Spinner animation="border" size="sm" />
                                                                                ) : (
                                                                                    'Send Mandate Link'
                                                                                )}
                                                                            </button>
                                                                        )}
                                                                        {linkUrl && (
                                                                            <button
                                                                                className="btn btn-sm btn-outline-success"
                                                                                title="Copy the mandate link — you can share it yourself via WhatsApp/SMS"
                                                                                onClick={() => handleCopyLink(m.loanId, linkUrl)}
                                                                            >
                                                                                {copiedLoanId === m.loanId ? <BsClipboardCheck /> : <BsClipboard />}
                                                                            </button>
                                                                        )}
                                                                        <button
                                                                            className="btn btn-sm btn-outline-dark"
                                                                            title="View this loan's full charge and reminder history"
                                                                            onClick={() => handleViewHistory(m.loanId)}
                                                                        >
                                                                            <BsClockHistory />
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            )
                                        ) : (
                                            <p className="empty-state">
                                                {activeTab === 'success' && "No mandate has succeeded yet."}
                                                {activeTab === 'pending' && "No mandate is pending confirmation right now."}
                                                {activeTab === 'failed' && "No failed/rejected mandates — nice."}
                                                {activeTab === 'all' && "No mandates registered yet."}
                                            </p>
                                        )}
                                    </div>

                                    {/* Pagination */}
                                    <div className="pagination-container">
                                        <div className="pagination-box">
                                            {startPage > 1 && (
                                                <>
                                                    <button onClick={() => setCurrentPage(1)}>1</button>
                                                    <span className="dots">...</span>
                                                </>
                                            )}
                                            {Array.from({ length: endPage - startPage + 1 }, (_, i) => (
                                                <button
                                                    key={startPage + i}
                                                    className={currentPage === startPage + i ? 'active' : ''}
                                                    onClick={() => setCurrentPage(startPage + i)}
                                                >
                                                    {startPage + i}
                                                </button>
                                            ))}
                                            {endPage < totalPages && (
                                                <>
                                                    <span className="dots">...</span>
                                                    <button onClick={() => setCurrentPage(totalPages)}>{totalPages}</button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Modal show={historyLoanId !== null} onHide={() => setHistoryLoanId(null)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Loan #{historyLoanId} — History</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {historyLoading ? (
                        <div className="text-center py-4"><Spinner animation="border" /></div>
                    ) : (
                        <>
                            <h6>Charge Attempts</h6>
                            {chargeHistory.length > 0 ? (
                                <table className="styled-table mb-4">
                                    <tbody>
                                        <tr>
                                            <th>Date</th>
                                            <th>Type</th>
                                            <th>Amount</th>
                                            <th>Status</th>
                                            <th>Error</th>
                                        </tr>
                                        {chargeHistory.map((c, i) => (
                                            <tr key={i}>
                                                <td>{formatDateTime(c.createDate) || '-'}</td>
                                                <td>{c.attemptType}{c.attemptSlot ? ` (${c.attemptSlot})` : ''}</td>
                                                <td>₹{c.amount ?? '-'}</td>
                                                <td><StatusBadge status={c.status} /></td>
                                                <td>{c.errorDescription || '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <p className="empty-state">No charge attempts yet.</p>
                            )}

                            <h6>Reminder Notifications</h6>
                            {notificationHistory.length > 0 ? (
                                <table className="styled-table">
                                    <tbody>
                                        <tr>
                                            <th>Date</th>
                                            <th>EMI Due</th>
                                            <th>Channel</th>
                                            <th>Status</th>
                                            <th>Error</th>
                                        </tr>
                                        {notificationHistory.map((n, i) => (
                                            <tr key={i}>
                                                <td>{formatDateTime(n.createDate) || '-'}</td>
                                                <td>{n.emiDueDate || '-'}</td>
                                                <td>{n.channel}</td>
                                                <td><StatusBadge status={n.status} /></td>
                                                <td>{n.errorMessage || '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <p className="empty-state">No reminders sent yet.</p>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setHistoryLoanId(null)}>Close</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default AutoDebitMandates;
